import { useCallback } from 'react';
import { Counter } from '../../components/Counter';
import { DateRangePicker } from '../../components/DateRangePicker';
import { GuestPicker } from '../../components/GuestPicker';
import {
  CounterLabel,
  CounterRow,
  Dropdown as GuestDropdown,
} from '../../components/GuestPicker/GuestPicker.styles';
import { useDropdownGroup } from '../../hooks/useDropdownGroup';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import { DotIcon } from '../../icons/DotIcon';
import { SearchSmallIcon } from '../../icons/SearchSmallIcon';
import { CategoryOption, ChevronIcon } from './JourneyPicker.styles';
import {
  Bar,
  CategoryDropdown,
  CategoryWrapper,
  DatesWrapper,
  DotSeparator,
  DropdownAnchor,
  GuestsWrapper,
  Pill,
  SearchLabel,
  SearchPill,
  SearchWrapper,
} from './JourneyPickerDesktop.styles';
import type { JourneyPickerSharedProps } from './JourneyPickerView.types';

const DROPDOWN_KEYS = ['category', 'dates', 'guests'] as const;
type DropdownKey = (typeof DROPDOWN_KEYS)[number];

export interface JourneyPickerDesktopProps extends JourneyPickerSharedProps {
  columnView?: boolean;
  numberOfMonths?: 1 | 2;
}

export function JourneyPickerDesktop({
  showCategory,
  categories,
  category,
  currentCategoryLabel,
  onCategorySelect,
  fromDate,
  toDate,
  isSingleDate,
  datesLabel,
  onRangeSelect,
  displayGuests,
  guestMode,
  adults,
  children,
  guestsLabel,
  onAdultsChange,
  onChildrenChange,
  availability,
  isLoadingAvailability,
  onMonthChange,
  columnView,
  numberOfMonths,
  onSearch,
  searchLabel,
  datePickerLabels,
  guestPickerLabels,
}: JourneyPickerDesktopProps) {
  const { toggle, closeAll, setRef, isOpen } = useDropdownGroup<DropdownKey>(DROPDOWN_KEYS);

  const handleRangeSelect = useCallback(
    (checkIn: string, checkOut: string) => {
      onRangeSelect(checkIn, checkOut);
      if (isSingleDate || checkOut) closeAll();
    },
    [onRangeSelect, isSingleDate, closeAll]
  );

  const handleCategoryClick = useCallback(
    (id: string) => {
      closeAll();
      onCategorySelect(id);
    },
    [closeAll, onCategorySelect]
  );

  const handleSearchClick = useCallback(() => {
    closeAll();
    onSearch();
  }, [closeAll, onSearch]);

  return (
    <Bar>
      {showCategory && (
        <CategoryWrapper ref={setRef('category')}>
          <Pill type="button" onClick={() => toggle('category')}>
            {currentCategoryLabel}
            <ChevronIcon>
              <ChevronDownIcon />
            </ChevronIcon>
          </Pill>

          {isOpen('category') && (
            <CategoryDropdown>
              {categories!.map((cat) => (
                <CategoryOption
                  key={cat.id}
                  type="button"
                  $active={cat.id === category}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  {cat.label}
                </CategoryOption>
              ))}
            </CategoryDropdown>
          )}
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>
        </CategoryWrapper>
      )}

      <DatesWrapper ref={setRef('dates')} $isFirst={!showCategory}>
        <Pill type="button" onClick={() => toggle('dates')}>
          {datesLabel}
        </Pill>

        {isOpen('dates') && (
          <DropdownAnchor $alignLeft={numberOfMonths === 1}>
            <DateRangePicker
              checkIn={fromDate || null}
              checkOut={isSingleDate ? null : toDate || null}
              singleDateMode={isSingleDate}
              availability={availability}
              isLoading={isLoadingAvailability}
              onRangeSelect={handleRangeSelect}
              onMonthChange={onMonthChange}
              columnView={columnView}
              numberOfMonths={numberOfMonths}
              renderInline
              labels={datePickerLabels}
            />
          </DropdownAnchor>
        )}
      </DatesWrapper>

      {displayGuests && (
        <>
          <DotSeparator aria-hidden>
            <DotIcon />
          </DotSeparator>

          {guestMode === 'combined' ? (
            <GuestsWrapper ref={setRef('guests')}>
              <Pill type="button" onClick={() => toggle('guests')}>
                {guestsLabel}
              </Pill>

              {isOpen('guests') && (
                <DropdownAnchor>
                  <GuestDropdown $inline>
                    <CounterRow>
                      <CounterLabel>{guestPickerLabels?.guests ?? 'Guests'}</CounterLabel>
                      <Counter
                        value={adults}
                        min={1}
                        max={20}
                        onChange={onAdultsChange}
                        decreaseLabel={guestPickerLabels?.decreaseAdults}
                        increaseLabel={guestPickerLabels?.increaseAdults}
                      />
                    </CounterRow>
                  </GuestDropdown>
                </DropdownAnchor>
              )}
            </GuestsWrapper>
          ) : (
            <GuestsWrapper ref={setRef('guests')}>
              <Pill type="button" onClick={() => toggle('guests')}>
                {guestsLabel}
              </Pill>

              {isOpen('guests') && (
                <DropdownAnchor>
                  <GuestPicker
                    adults={adults}
                    childCount={children}
                    onAdultsChange={onAdultsChange}
                    onChildrenChange={onChildrenChange}
                    renderInline
                    labels={guestPickerLabels}
                  />
                </DropdownAnchor>
              )}
            </GuestsWrapper>
          )}
        </>
      )}
      <DotSeparator aria-hidden>
        <DotIcon />
      </DotSeparator>
      <SearchWrapper>
        <SearchPill type="button" onClick={handleSearchClick}>
          <SearchSmallIcon />
          <SearchLabel data-text={searchLabel}>{searchLabel}</SearchLabel>
        </SearchPill>
      </SearchWrapper>
    </Bar>
  );
}
